import { useEffect } from 'react';

const useWebVitals = () => {
  useEffect(() => {
    // Performance observer for Core Web Vitals
    const observeWebVitals = () => {
      // Largest Contentful Paint (LCP)
      try {
        const lcpObserver = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          const lastEntry = entries[entries.length - 1];
          
          // Send LCP data to analytics (replace with your analytics service)
          if (window.gtag) {
            window.gtag('event', 'web_vitals', {
              name: 'LCP',
              value: Math.round(lastEntry.startTime),
              event_category: 'Web Vitals'
            });
          }
          
          console.log('LCP:', lastEntry.startTime);
        });
        
        lcpObserver.observe({ entryTypes: ['largest-contentful-paint'] });
      } catch (e) {
        console.warn('LCP measurement not supported');
      }

      // First Input Delay (FID)
      try {
        const fidObserver = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          entries.forEach((entry) => {
            const fid = entry.processingStart - entry.startTime;
            
            // Send FID data to analytics
            if (window.gtag) {
              window.gtag('event', 'web_vitals', {
                name: 'FID',
                value: Math.round(fid),
                event_category: 'Web Vitals'
              });
            }
            
            console.log('FID:', fid);
          });
        });
        
        fidObserver.observe({ entryTypes: ['first-input'] });
      } catch (e) {
        console.warn('FID measurement not supported');
      }

      // Cumulative Layout Shift (CLS)
      try {
        let clsValue = 0;
        let clsEntries = [];
        
        const clsObserver = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          
          entries.forEach((entry) => {
            if (!entry.hadRecentInput) {
              clsEntries.push(entry);
              clsValue += entry.value;
            }
          });
          
          // Send CLS data to analytics
          if (window.gtag) {
            window.gtag('event', 'web_vitals', {
              name: 'CLS',
              value: Math.round(clsValue * 1000),
              event_category: 'Web Vitals'
            });
          }
          
          console.log('CLS:', clsValue);
        });
        
        clsObserver.observe({ entryTypes: ['layout-shift'] });
      } catch (e) {
        console.warn('CLS measurement not supported');
      }

      // Time to First Byte (TTFB)
      try {
        const navigationObserver = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          entries.forEach((entry) => {
            const ttfb = entry.responseStart - entry.requestStart;
            
            // Send TTFB data to analytics
            if (window.gtag) {
              window.gtag('event', 'web_vitals', {
                name: 'TTFB',
                value: Math.round(ttfb),
                event_category: 'Web Vitals'
              });
            }
            
            console.log('TTFB:', ttfb);
          });
        });
        
        navigationObserver.observe({ entryTypes: ['navigation'] });
      } catch (e) {
        console.warn('TTFB measurement not supported');
      }
    };

    // Optimize critical rendering path
    const optimizeCriticalPath = () => {
      // Preload critical resources
      const preloadFont = (href, crossorigin = true) => {
        const link = document.createElement('link');
        link.rel = 'preload';
        link.as = 'font';
        link.href = href;
        link.type = 'font/woff2';
        if (crossorigin) link.crossOrigin = 'anonymous';
        document.head.appendChild(link);
      };

      // Preconnect to external domains
      const preconnect = (href) => {
        const link = document.createElement('link');
        link.rel = 'preconnect';
        link.href = href;
        document.head.appendChild(link);
      };

      // Add DNS prefetch for external resources
      const dnsPrefetch = (href) => {
        const link = document.createElement('link');
        link.rel = 'dns-prefetch';
        link.href = href;
        document.head.appendChild(link);
      };

      // Preconnect to external services
      preconnect('https://api.bagi.rs');
      preconnect('https://maps.googleapis.com');
      
      // DNS prefetch for social media and external assets
      dnsPrefetch('https://www.facebook.com');
      dnsPrefetch('https://www.instagram.com');
      dnsPrefetch('https://www.youtube.com');
    };

    // Implement resource hints
    const implementResourceHints = () => {
      // Critical CSS inline
      const inlineCriticalCSS = () => {
        // This would typically be done at build time
        // But we can add some critical styles here
        const criticalCSS = `
          body { font-display: swap; }
          img { height: auto; max-width: 100%; }
          .lazy-image-container { background-color: #f0f0f0; }
        `;
        
        const style = document.createElement('style');
        style.textContent = criticalCSS;
        document.head.appendChild(style);
      };

      inlineCriticalCSS();
    };

    // Run optimizations
    if (typeof window !== 'undefined') {
      observeWebVitals();
      optimizeCriticalPath();
      implementResourceHints();
    }

    // Cleanup function
    return () => {
      // Disconnect any observers if needed
    };
  }, []);

  // Return a function to manually report custom metrics
  const reportCustomMetric = (name, value, category = 'Custom') => {
    if (window.gtag) {
      window.gtag('event', 'custom_metric', {
        name: name,
        value: Math.round(value),
        event_category: category
      });
    }
    console.log(`Custom metric - ${name}:`, value);
  };

  return { reportCustomMetric };
};

export default useWebVitals;
